class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = document.querySelector("[data-search-popup]");

    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.abortController = null;
    this.requestId = 0;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    popularSearchItems.forEach((itm) => (itm.href = createSearchLink(itm.dataset.psQuery)));
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");
    if (form) form.addEventListener("submit", this.onFormSubmit.bind(this));

    const debounceFn = (typeof debounce === 'function') ? debounce : (typeof window.debounce === 'function') ? window.debounce : (fn) => fn;
    if (this.input) {
      this.input.addEventListener(
        "input",
        debounceFn((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
    if (clear) clear.addEventListener("click", this.onClearSearch.bind(this));
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
  }

  getQuery() {
    return this.input?.value.trim() || "";
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.abortPendingRequest();
      this.requestId += 1;
      this.close(true);
      this.toggleSpinnerLoading(false);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onClearSearch(event) {
    event.preventDefault();
    if (this.input) this.input.value = "";
    this.onChange();
  }

  abortPendingRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  isCurrentRequest(requestId, searchTerm) {
    return requestId === this.requestId && this.getQuery() === searchTerm;
  }

  getSearchResults(searchTerm) {
    this.abortPendingRequest();
    const requestId = ++this.requestId;
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag = this.dataset.searchByTag === "true";
    const searchByBody = this.dataset.searchByBody === "true";
    const unavailableProductsOption = this.dataset.unavailableProductsOption;

    let searchFields = "title,product_type,vendor,variants.sku,variants.title";

    if (searchByTag) searchFields += ",tag";
    if (searchByBody) searchFields += ",body";

    if (this.cachedResults[queryKey]) {
      if (this.isCurrentRequest(requestId, searchTerm)) {
        this.renderSearchResults(this.cachedResults[queryKey]);
      }
      return;
    }

    const intlSearch =
      this.dataset.kpIntl === "true" ||
      (window.MinimogSettings &&
        window.MinimogSettings.routes &&
        window.MinimogSettings.routes.root &&
        window.MinimogSettings.routes.root !== "/");
    const resourceTypes = intlSearch ? "product,collection" : "product,collection,page,article";
    let searchURL = `${window.MinimogSettings.routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[type]=${resourceTypes}&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;

    this.abortController = new AbortController();

    fetch(`${searchURL}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!this.isCurrentRequest(requestId, searchTerm)) return null;
        if (!response.ok) {
          this.toggleSpinnerLoading(false);
          this.toggleClearSearch(Boolean(this.getQuery().length));
          this.close();
          return null;
        }
        return response.text();
      })
      .then((text) => {
        if (!text || !this.isCurrentRequest(requestId, searchTerm)) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search")?.innerHTML;
        if (!resultsMarkup) {
          this.toggleSpinnerLoading(false);
          this.toggleClearSearch(Boolean(this.getQuery().length));
          return;
        }
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        if (!this.isCurrentRequest(requestId, searchTerm)) return;
        this.toggleSpinnerLoading(false);
        this.toggleClearSearch(Boolean(this.getQuery().length));
        this.close();
        console.error(error);
      });
  }

  renderSearchResults(resultsMarkup) {
    window.KPTheme?.track('predictive_search', { query_length: this.getQuery().length });
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    if (searchItemsWrapper && searchItemsWrapper.childElementCount > 0) {
      this.renderSearchQueryAndMessage(true);
    } else {
      this.renderSearchQueryAndMessage(false);
    }

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
    if (!input || !searchQuery || !searchMessage) return;
    const query = input.value;

    const { resultsTitle } = searchMessage.dataset;
    searchQuery.textContent = query;

    this.predictiveSearchResults?.classList.remove("m:hidden");
    this.searchPopupSearults?.classList.remove("m:hidden");

    if (results) {
      searchMessage.textContent = resultsTitle;
      moreResultIcon?.classList.add("m:hidden");
    } else {
      searchMessage.textContent = searchMessage.dataset.resultsTitle;
      moreResultIcon?.classList.remove("m:hidden");
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    if (submit) submit.style.visibility = show ? "hidden" : "visible";
    if (loading) loading.style.visibility = show ? "visible" : "hidden";
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    if (clear) clear.style.visibility = show ? "visible" : "hidden";
  }

  open() {
    if (!this.searchPopupSearults || !this.input) return;
    this.searchPopupSearults.style.setProperty(
      "--search-result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
    );
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    this.popularSearches && this.popularSearches.classList.add("m:hidden");
    this.searchCount?.classList.remove("m:hidden");
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    this.predictiveSearchResults?.classList.add("m:hidden");
    this.searchPopupSearults?.classList.add("m:hidden");
    this.popularSearches && this.popularSearches.classList.remove("m:hidden");
    this.searchCount?.classList.add("m:hidden");
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - this.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
}

customElements.define("predictive-search", PredictiveSearch);
